import { useContext, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useLoaderData, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  ListItem,
  Typography,
  useTheme,
  Modal,
  Divider,
  Tooltip,
  Skeleton,
  IconButton,
  TextField,
  CircularProgress,
  Menu,
} from '@mui/material';
import { NoteSection } from '../components/NoteSection';
import { NotepadBox } from '../components/ui/NotepadBox';
import {
  Delete as DeleteIcon,
  Check as CheckIcon,
  ContentCopy as ContentCopyIcon,
  Spellcheck as SpellcheckIcon,
  Edit,
  Check,
  KeyboardArrowRightOutlined,
  KeyboardArrowLeftOutlined,
  Menu as MenuIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { SVGIcon } from '../components/SVGIcon';
import { formatDate, formatDuration, textToParagraph, copyRichText } from '../utils/utils';
import { DebugContext } from '../context/DebugContext';
import { Transcript } from '../components/Transcript';
import { useUIState } from '../context/uiState';
import { DeleteModal } from '../components/DeleteModal';
import EmailNoteModal from '../components/EmailNoteModal';
import { NoteFeedbackBox } from '../components/NoteFeedbackBox';
import {
  AdminEmails,
  Encounter,
  NoteDataType,
  NoteExtraType,
  NoteSectionType,
  Patient,
} from '../types/types';
import { RoundedButton } from '../styles/CustomButtons';
import React from 'react';
import { useUser, useAccessToken } from '@features/user';
import { useEventTracking, useFeatureGate } from '@services/statsig';
import { useUserPreferences } from '@features/user-preferences';
import {
  EditAllBox,
  useNoteWithPolling,
  useUpdateNoteMutation,
  useUpdateNoteSectionMutation,
  useRevertNoteSectionMutation,
  notesApi,
  useEditTemplateLabelExperiment,
} from '@features/notes';
import APIService from '../services/APIService';
import { getNoteRoute } from '../utils/noteRouting';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector, useModal } from '@store/hooks';
import { useBrandLanguage } from '@context/brand-language/context';
import SpellModal from '../components/modals/SpellModal';
import { NoteMetadata } from '../components/NoteMetadata';
import { MODAL_TYPES } from '../components/modals';
import { clearFindReplace } from '../store/slices/findReplaceSlice';
import { findReplaceInSections } from '../utils/findReplace';
import { removeScrollBar } from '../styles/globalStyles';
import AssignNoteToPatient from '../components/AssignNoteToPatient';
import LayoutWrapper from '../components/layout/UILayout';
import SlideSidePane from './Shared/SlideSidePane';
import BillingSubmissionView from './BillingSubmissionView';
import PatientInformationView from '../components/Patient/PatientInformationView';
import PatientUnassignment from '../components/PatientUnassignment';
import PatientEligibilityView from '../components/Patient/PatientEligibilityView';
import AssignPatientToNoteDialog from '../components/Patient/AssignPatientToNoteDialog';
import { trackEvent as trackLegacyEvent } from '../utils/analytics_utils';
import ModuleConversationView from '../components/ModuleConversation/ModuleConversationView';
import { useSnackbar } from 'notistack';
import NoteDocumentPicker from '../components/Note/NoteDocumentPicker';
import { marked } from 'marked';
import removeMd from 'remove-markdown';
import { TutorialProvider } from '../components/Tutorial/TutorialContext';
import { TutorialStep } from '../components/Tutorial/TutorialStep';
import { NoteLoader } from '../loaders/NoteLoader';
import { DemoNoteLoader } from './DemoNoteView';
import SwitchTemplateButton from '../components/Note/SwitchTemplateButton';
import SigningNoteOnboarding from '../components/Note/SigningNoteOnboarding';
import { useToolbar } from '@features/toolbar';
import { useGetEncounterQuery, encountersApi } from '@features/encounters';
import { useNoteSigningEnabled } from '@features/user';
import { useAuditEnabled } from '@features/jotaudit';
import {
  buildSelectionCopied,
  type TelemetryContext,
} from '../utils/analytics/noteTelemetryBuilder';
import { trackNoteTelemetry, NoteTelemetryService } from '../utils/analytics/noteTelemetry';
import { extractTemplateInfo, getTemplateId, getTemplateName } from '@features/templates/utils';

interface Section {
  id: string;
}

// Loaders moved to ../loaders/NoteLoader.ts to break circular dependencies
export { MainNoteLoader as NoteLoader, DemoNoteLoader } from '../loaders/NoteLoader';

interface NoteViewProps {
  demoMode?: boolean;
}

interface ModuleCustomizationModalInfo {
  userFeedback?: string;
  showModal: boolean;
  module_id: string;
  note_id: string;
  initialComparisonText: string;
  section_name: string;
  sectionIndex: number;
}

function NoteViewRevamp({ demoMode = false }: NoteViewProps) {
  const win: Window = window;
  const theme = useTheme();
  const debug = useContext(DebugContext);
  const { noteId } = useParams();

  // For demo mode, use loader data
  const demoNote = demoMode ? (useLoaderData() as any) : null;

  // Use RTK Query to fetch note data with auto-polling (skip if demo mode)
  const {
    data: v2Note,
    isLoading,
    isError,
    error: queryError,
    refetch: refetchNote,
  } = useNoteWithPolling(noteId, {
    skip: demoMode || !noteId,
  });

  // RTK Query mutations for updating notes
  const [updateNote] = useUpdateNoteMutation();
  const [updateNoteSection] = useUpdateNoteSectionMutation();
  const [revertNoteSection] = useRevertNoteSectionMutation();

  const [selectedDocId, setSelectedDocId] = useState<string | undefined>(noteId);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const { open: openModal } = useModal();
  const findReplaceAction = useAppSelector(state => state.findReplace.action);
  const { preferences: userPreferences } = useUserPreferences();

  // Demo mode needs local state since it doesn't use RTK Query
  const [demoNoteData, setDemoNoteData] = useState<NoteDataType | null>(
    demoMode ? (demoNote as NoteDataType) : null,
  );

  // Use RTK Query cache directly - no local state needed for production!
  // For demo mode, use local state. Otherwise, use v2Note from RTK Query cache.
  const noteData = demoMode ? demoNoteData : (v2Note as unknown as NoteDataType | null);

  // Patient data comes from RTK Query cache (production) or demoNoteData (demo mode)
  // The v2 API includes patient in the note response!
  const patientData = demoMode
    ? (((demoNoteData as any)?.patient as Patient | null) ?? null)
    : ((v2Note?.patient as unknown as Patient | null) ?? null);

  // Extract encounter_id from note response
  // Primary: v2Note.encounter_id (if backend adds it)
  // Fallback: v2Note.encounter?.encounter_id (current structure)
  const encounterIdFromNote = demoMode
    ? (demoNoteData as any)?.encounter_id || (demoNoteData as any)?.encounter?.encounter_id
    : (v2Note as any)?.encounter_id || (v2Note?.encounter as any)?.encounter_id;

  // Fetch encounter data using RTK Query when we have encounterId
  const { data: encounterData } = useGetEncounterQuery(
    { encounterId: encounterIdFromNote! },
    { skip: !encounterIdFromNote || demoMode },
  );

  // Extract encounter_id - use from note response first, fallback to RTK Query result
  const encounterId = encounterIdFromNote || encounterData?.encounter_id;

  // Update editedTitle when noteData loads
  useEffect(() => {
    if (noteData?.note_title) {
      setEditedTitle(noteData.note_title);
    }
  }, [noteData?.note_title]);

  // Sync selectedDocId with noteId when noteId changes
  useEffect(() => {
    if (noteId) {
      setSelectedDocId(noteId);
    }
  }, [noteId]);

  const [added, setAdded] = useState<number[]>([]);
  const [saving, setSaving] = useState<number[]>([]);
  const [viewMode, setViewMode] = useState<{
    mode: 'note' | 'transcript';
  }>({
    mode: 'note',
  });
  const { state: uiState, setState: setUIState, showAlertBanner } = useUIState();
  const { loadedInChromeExtension } = uiState;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pdfGenerating, setPDFGenerating] = useState(false);

  const [activeSection, setActiveSection] = useState<Section>();
  const scrollRef = useRef<HTMLDivElement>(null);

  const [emailList, setEmailList] = useState<AdminEmails[]>([]);
  const [emailModalOpen, setEmailModalOpen] = useState<boolean>(false);
  const [sendPDFEmailList, setSendPDFEmailList] = useState<string[]>([]);
  const [loadingText, setLoadingText] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [copiedInSession, setCopiedInSession] = useState(false);
  const [loadingSections, setLoadingSections] = useState<string[]>();
  const [editInstructions, setEditInstructions] = useState<string>('');
  const [patRel, setPatRel] = useState<string>('self');

  const [showChromeExtensionTooltips, setShowChromeExtensionTooltips] = useState<boolean>(true);
  const [showNoteOnboarding, setShowNoteOnboarding] = useState<boolean>(false);
  const [selectedSections, setSelectedSections] = useState<number[]>([]);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(noteData?.note_title || '');

  const noteSigningEnabled = useNoteSigningEnabled();

  // Check if audit features are enabled
  const isAuditEnabled = useAuditEnabled();

  // Check if Edit Template feature is enabled (Statsig)
  const showEditTemplate = useFeatureGate('feature_edit_template_from_note');
  // Get the button label from the experiment
  const editButtonLabel = useEditTemplateLabelExperiment();

  const [hasEncounterPollingExpired, setHasEncounterPollingExpired] = useState(false);

  // Use encounterId extracted from note response or RTK Query result
  const encounterIdForPolling = encounterId;

  const encounterOutputsCount = encounterData?.outputs?.length ?? 0;

  // Check if there are any pending notes in the outputs (for display purposes)
  const hasPendingNotes = useMemo(() => {
    if (!encounterData?.outputs) return false;
    return encounterData.outputs.some(
      (output: any) => output.type?.toLowerCase() === 'note' && output.status === 'pending',
    );
  }, [encounterData?.outputs]);

  // Poll when feature flag is enabled and:
  // 1. There is exactly 1 output (waiting for payer aware note), OR
  // 2. There are pending notes (waiting for them to complete)
  // Matches logic from NoteChatView.tsx
  const encounterPollingEnabled =
    Boolean(noteId) &&
    isAuditEnabled &&
    !demoMode &&
    (encounterOutputsCount === 1 || hasPendingNotes);
  const shouldPollEncounterDocuments = encounterPollingEnabled && !hasEncounterPollingExpired;

  // Debug logging for encounter data updates
  useEffect(() => {
    if (encounterData) {
      console.log('[NoteViewRevamp] Encounter data updated', {
        encounterId: encounterData.encounter_id,
        outputsCount: encounterData.outputs?.length ?? 0,
        outputs: encounterData.outputs?.map((o: any) => ({
          type: o.type,
          status: o.status,
          encounter_document_id: o.encounter_document_id,
          jotdoc_id: o.jotdoc_id,
          title: o.title,
        })),
        timestamp: new Date().toISOString(),
      });
    }
  }, [encounterData]);

  // Registry for BlockNote editors from markdown sections
  const blockNoteEditorsRef = useRef<Map<number, any>>(new Map());

  // Track view mode changes for analytics
  const viewModeTimestampRef = useRef<{ timestamp: number; mode: string }>({
    timestamp: Date.now(),
    mode: viewMode.mode,
  });

  // Clean up editor references when sections change
  useEffect(() => {
    return () => {
      blockNoteEditorsRef.current.clear();
    };
  }, [noteData?.content?.sections]);

  const [tutorialHeaderText, setTutorialHeaderText] = useState<string>(
    noteData?.completion_status === 'completed'
      ? 'Note complete!'
      : 'See something you want to change?',
  );
  const [tutorialDescription, setTutorialDescription] = useState<string>(
    noteData?.completion_status === 'completed'
      ? "JotPsych has many more features to help you get your admin work done faster and more accurately. We recommend you check out the template builder if you haven't already. Happy charting!"
      : "Teach JotPsych how to write better notes! Click the 'edit' button in any section to interact with the JotPsych AI.",
  );
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);

  const [moduleCustomizationModalInfo, setModuleCustomizationModalInfo] =
    useState<ModuleCustomizationModalInfo>({
      showModal: false,
      module_id: '',
      initialComparisonText: '',
      note_id: noteId || '',
      section_name: '',
      sectionIndex: 0,
    });

  // Analytics hooks
  const { note: noteMetrics, performance } = useEventTracking();
  const viewStartTimeRef = useRef<number | undefined>(undefined);
  const editStartTimeRef = useRef<number | undefined>(undefined);
  const sectionEditCountRef = useRef<number>(0);
  const lastNoteViewedKeyRef = useRef<string | null>(null);

  const location = useLocation();
  const origin = location.state?.origin; // Access the 'origin' property from state
  const [misspelledWords, setMisspelledWords] = useState<string[]>([]);

  // Register encounter_id with telemetry service when it becomes available or changes
  // This gates flush until encounter_id is set and auto-enriches all events
  useEffect(() => {
    if (encounterId) {
      NoteTelemetryService.setEncounterId(encounterId);
    }
  }, [encounterId]);

  // Cleanup telemetry listeners/timers on unmount
  useEffect(() => {
    return () => {
      NoteTelemetryService.dispose();
    };
  }, []);

  // Track when note is viewed (BlockNote analytics) - fire each time viewMode switches to 'note'
  useEffect(() => {
    const viewKey = `${viewMode.mode}:${noteId}`;
    if (
      noteId &&
      !demoMode &&
      viewMode.mode === 'note' &&
      lastNoteViewedKeyRef.current !== viewKey
    ) {
      lastNoteViewedKeyRef.current = viewKey;
      trackNoteTelemetry('note_viewed', {
        docId: `note:${noteId}`,
        docVersion: '0',
        metadata: {
          note_id: noteId,
          encounter_id: encounterId,
        },
      });
    }
  }, [noteId, demoMode, viewMode.mode, encounterId]);

  // Track when note is viewed
  useEffect(() => {
    if (noteId && !demoMode) {
      // Track single NOTE_VIEWED with all metadata
      noteMetrics.viewed(noteId, {
        source: origin || 'direct',
      });

      // Start timer for time spent on note
      viewStartTimeRef.current = performance.now();
      editStartTimeRef.current = Date.now();
    }

    return () => {
      if (noteId && !demoMode && viewStartTimeRef.current) {
        const duration = performance.now() - viewStartTimeRef.current;

        // Track time spent on note
        performance.timing(`note_view_${noteId}`, duration, {
          note_id: noteId,
        });

        // Session ended tracking removed - duration already tracked via performance.timing
      }
    };
  }, [noteId, demoMode]);

  const [matchedWords, setMatchedWords] = useState<string[]>([]);
  const [matchedMisspelled, setMatchedMisspelled] = useState<string[]>([]);
  const [misspellingMap, setMisspellingMap] = useState<{ [correctSpelling: string]: string[] }>({});
  const [selectedWord, setSelectedWord] = useState<string | null>(null);
  const { troubleWords, subjectLanguage } = useUser();

  const { userState, updateOnboardingStep, fetchUserInfo } = useUser();
  const { getAccessToken } = useAccessToken();
  const { brandLanguage } = useBrandLanguage();
  const { setContext: setToolbarContext, show: showToolbar, hide: hideToolbar } = useToolbar();
  const [templateIdToRegenerate, setTemplateIdToRegenerate] = useState<string>();
  const [isRegeneratingNote, setIsRegeneratingNote] = useState(false);

  const [isSwitchTemplateCard, setIsSwitchTemplateCard] = useState(false);

  const [missingPatientEligibilityFields, setMissingPatientEligibilityFields] = useState<string[]>(
    [],
  );
  const [missingPatientBillingFields, setMissingPatientBillingFields] = useState<string[]>([]);
  const [missingProviderEligibilityFields, setMissingProviderEligibilityFields] = useState<
    string[]
  >([]);
  const [missingProviderBillingFields, setMissingProviderBillingFields] = useState<string[]>([]);
  const [openAssignPatientToNoteDialog, setOpenAssignPatientToNoteDialog] = useState(false);

  const handlePatientEligibilitySave = (newPatRel: string) => {
    setPatRel(newPatRel);
    setIsPatientEligibilitySidePaneOpen(false);
  };

  const [isPatientInformationSidePaneOpen, setIsPatientInformationSidePaneOpen] = useState(false);

  const [isPatientEligibilitySidePaneOpen, setIsPatientEligibilitySidePaneOpen] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAssignPatientClick = () => {
    setOpenAssignPatientToNoteDialog(true);
  };

  const handleOpenPatientInformationSidePane = () => {
    setIsPatientInformationSidePaneOpen(true);
  };

  const handleOpenPatientEligibilitySidePane = () => {
    setIsPatientEligibilitySidePaneOpen(true);
  };

  const handleClosePatientEligibilitySidePane = () => {
    setIsPatientEligibilitySidePaneOpen(false);
  };

  const handleClosePatientInformationSidePane = () => {
    setIsPatientInformationSidePaneOpen(false);
  };

  const [isBillingSidePaneOpen, setIsBillingSidePaneOpen] = useState(false);

  const handleOpenBillingSidePane = () => {
    trackLegacyEvent({
      event: 'submit_billing_click',
      event_category: 'Billing',
      event_label: 'Submit Billing on Note Page',
    });
    setIsBillingSidePaneOpen(true);
  };

  const handleCloseBillingSidePane = () => {
    setIsBillingSidePaneOpen(false);
  };

  const handleBillingSubmitted = () => {
    setBillingSubmitted(true);
    handleCloseBillingSidePane();
  };

  const [billingSubmitted, setBillingSubmitted] = useState(false);

  useEffect(() => {
    if (noteData?.billing_claim_status) {
      if (noteData.billing_claim_status === 'completed') {
        setBillingSubmitted(true);
      }
    }
  }, [noteData]);

  const handlePatientInfoClick = () => {
    if (patientData) {
      handleOpenPatientInformationSidePane();
    }
  };

  const handlePatientEligibilityClick = () => {
    if (
      patientData &&
      missingPatientEligibilityFields.length === 0 &&
      missingProviderEligibilityFields.length === 0 &&
      patientData.eligibility === 'yes'
    ) {
      handleOpenPatientEligibilitySidePane();
    }
  };

  const handleAdd = (sectionIndex: number) => {
    if (added.includes(sectionIndex)) {
      setAdded(current => current.filter(section => section !== sectionIndex));
    } else {
      setAdded(current => [...current, sectionIndex].sort((a, b) => a - b)); // Add new section index and sort the array
    }
  };

  const handleUpdateTitle = async () => {
    if (demoMode) {
      setDemoNoteData(prevData => ({ ...prevData, note_title: editedTitle }) as NoteDataType);
      setIsEditingTitle(false);
      return;
    }

    // Close the editing UI immediately
    setIsEditingTitle(false);

    try {
      // Use RTK Query mutation - onQueryStarted handles optimistic update
      await updateNote({
        noteId: noteId!,
        updateNoteRequest: {
          note_title: editedTitle,
        },
      }).unwrap();
    } catch (error) {
      console.error('Failed to update note title:', error);
      enqueueSnackbar('Failed to update note title', { variant: 'error' });
    }
  };

  const handleSaveEdits = async (sectionIndex: number, editedText: string) => {
    setTutorialHeaderText('Transferring the note to your EHR');
    setTutorialDescription(
      "Once you're done making edits to your note, let's look at how to copy it to your EHR. On the top toolbar, you can press 'Copy all sections' to grab the entire note. If you just want some sections, you can click the + icon next to each section that you want, and then press 'Copy selected sections'. You can also use JotTransfer to push notes directly into your EHR.",
    );

    // Track the edit
    const section = noteData!.content.sections[sectionIndex];
    const originalText = section.text;
    const wasEdited = editedText !== originalText;

    if (wasEdited && !demoMode) {
      sectionEditCountRef.current += 1;

      noteMetrics.edit(noteId || '', {
        edit_type: 'manual',
        changes_count: 1,
      });
    }

    // Demo mode: update local state
    if (demoMode) {
      setDemoNoteData(prevNoteData => {
        const updatedSections = [...prevNoteData!.content.sections];
        updatedSections[sectionIndex] = {
          ...updatedSections[sectionIndex],
          edited_text: editedText,
        };
        return {
          ...prevNoteData!,
          content: { ...prevNoteData!.content, sections: updatedSections },
        };
      });
      return;
    }

    // Production mode: RTK Query mutation with optimistic update via onQueryStarted
    setSaving([...saving, sectionIndex]);

    try {
      await updateNoteSection({
        noteId: noteId!,
        sectionIndex,
        editSectionRequest: {
          edited_text: editedText,
        },
      }).unwrap();

      setSaving(current => current.filter(section => section !== sectionIndex));
    } catch (error) {
      console.error('Failed to save section edit:', error);
      setSaving(current => current.filter(section => section !== sectionIndex));
      enqueueSnackbar('Failed to save section edit', { variant: 'error' });
    }
  };

  useEffect(() => {
    if (findReplaceAction && noteData?.content?.sections) {
      const { updatedSections } = findReplaceInSections(
        noteData.content.sections,
        findReplaceAction.find,
        findReplaceAction.replace,
        findReplaceAction.caseSensitive,
      );
      updatedSections.forEach((section, index) => {
        const newText = section.edited_text || section.text;
        const originalText =
          noteData.content.sections[index].edited_text || noteData.content.sections[index].text;
        if (newText !== originalText) {
          handleSaveEdits(index, newText);
        }
      });

      // Demo mode: update local state
      if (demoMode) {
        setDemoNoteData(prev => {
          if (!prev) return null;
          return {
            ...prev,
            content: {
              ...prev.content,
              sections: updatedSections.map(section => ({
                ...section,
                edited_text: section.edited_text ?? undefined,
                edits: section.edits ?? [],
                error: section.error ?? undefined,
                context: section.context ?? undefined,
                markdown: section.markdown ?? undefined,
                model: section.model ?? undefined,
                module_id: section.module_id ?? '',
                priority: section.priority ?? undefined,
                is_rated: section.is_rated ?? false,
              })),
            },
          };
        });
      }
      // Production mode: handleSaveEdits already triggers RTK Query mutations with optimistic updates

      dispatch(clearFindReplace());
    }
  }, [findReplaceAction, noteData?.content?.sections, dispatch, demoMode]);

  const handleRevertEdits = async (sectionIndex: number) => {
    // Demo mode: update local state
    if (demoMode) {
      setDemoNoteData(prevNoteData => {
        const updatedSections = [...prevNoteData!.content.sections];
        updatedSections[sectionIndex] = {
          ...updatedSections[sectionIndex],
          edited_text: '',
        };
        return {
          ...prevNoteData!,
          content: { ...prevNoteData!.content, sections: updatedSections },
        };
      });
      return;
    }

    // Production mode: RTK Query mutation with optimistic update via onQueryStarted
    setSaving([...saving, sectionIndex]);

    try {
      await revertNoteSection({
        noteId: noteId!,
        sectionIndex,
      }).unwrap();

      setSaving(current => current.filter(section => section !== sectionIndex));
    } catch (error) {
      console.error('Failed to revert section:', error);
      setSaving(current => current.filter(section => section !== sectionIndex));
      enqueueSnackbar('Failed to revert section', { variant: 'error' });
    }
  };

  const handleSetCopyStatus = async () => {
    if (demoMode) {
      return;
    }

    if (!copiedInSession && !noteData?.is_copied) {
      // only update is_copied to true if not already set
      const accessToken = await getAccessToken();

      const response = await APIService.makeAPIPostRequest({
        requestString: '/notes/setCopyStatus',
        accessToken: accessToken,
        body: { note_id: noteId },
      });

      if (response.ok) {
        setCopiedInSession(true);
      }
    }
  };

  const handleSendCopyEvents = async (module_ids: string[], copied_all: boolean) => {
    if (module_ids.length === 0) {
      // exit the function if no module_ids are present
      return;
    }

    const accessToken = await getAccessToken();

    const response = await APIService.makeAPIPostRequest({
      requestString: '/notes/recordCopyEvent',
      accessToken: accessToken,
      body: {
        note_id: noteId,
        module_ids: module_ids,
        copied_all: copied_all,
      },
    });
  };

  const handleSectionClick = (sectionIndex: number) => {
    const sectionElement = document.getElementById(`section-${sectionIndex}`);
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: 'smooth' });
    }
    setActiveSection({ id: String(sectionIndex) });
    setUIState({
      ...uiState,
      sidebar: { isHidden: true },
    });
  };

  const handleSidebarIconClick = (event: React.MouseEvent, sectionIndex: number) => {
    // Prevent event propagation
    event.stopPropagation();

    handleAddSection(sectionIndex);
  };

  const handleAddSection = (sectionIndex: number) => {
    // Update both arrays to maintain visual state and copy functionality
    setAdded(prevAdded => {
      if (prevAdded.includes(sectionIndex)) {
        return prevAdded.filter(index => index !== sectionIndex);
      } else {
        return [...prevAdded, sectionIndex].sort((a, b) => a - b);
      }
    });

    setSelectedSections(prevSelectedSections => {
      if (prevSelectedSections.includes(sectionIndex)) {
        return prevSelectedSections.filter(index => index !== sectionIndex);
      } else {
        return [...prevSelectedSections, sectionIndex];
      }
    });
  };

  const titleCase = (sentence: string) => {
    let trimmedSentence = sentence.trim();

    const words = trimmedSentence.split(' ');

    for (let i = 0; i < words.length; i++) {
      words[i] = words[i][0].toUpperCase() + words[i].substr(1);
    }

    const newSentence = words.join(' ');

    return newSentence;
  };

  const handleOpenCopyPasteSettings = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the copy action
    openModal(MODAL_TYPES.COPY_PASTE_PREFERENCES, {});
  };

  const handleCopySections = async (copyAll = false) => {
    if (!noteData) return; // Safety check for TypeScript

    let htmlString = '';
    let plainTextString = '';
    let moduleIds: string[] = [];

    // Get user preferences for copying (using the hook value from component scope)
    // Type assertion until backend types are regenerated
    const prefs = userPreferences as any;
    const includeHeaderOnCopyMultiple =
      prefs?.copy_paste_preferences?.include_header_on_copy_multiple ?? true;
    const includeHtml = prefs?.copy_paste_preferences?.html_formatting_on_copy ?? true;

    // Check if there are any sections in the note
    const totalSections = noteData.content.sections.length;
    if (totalSections === 0) {
      enqueueSnackbar('No sections available to copy', {
        variant: 'warning',
      });
      return;
    }

    // Check if user is trying to copy selected sections but hasn't selected any
    if (!copyAll && selectedSections.length === 0) {
      enqueueSnackbar('No sections selected. Please select sections to copy.', {
        variant: 'warning',
      });
      return;
    }

    setTutorialHeaderText('Complete the note');
    setTutorialDescription(
      "Now, the note is on your clipboard and ready to be pasted into your EHR. As a final step, let's make sure the note is associated with a patient (if applicable) and then let's press 'Complete Note' in the bottom right toolbar. This moves the note from 'reviewing' to 'completed'.",
    );

    // Determine start time with fallback chain:
    // 1. encounterData.start_time
    // 2. encounterData.appointment.start_time
    // 3. noteData.transcription_start_timestamp
    // 4. noteData.time_uploaded
    let startTimeEpoch: number | null = null;
    if (encounterData?.start_time) {
      startTimeEpoch = new Date(encounterData.start_time).getTime() / 1000;
    } else if (encounterData?.appointment?.start_time) {
      startTimeEpoch = new Date(encounterData.appointment.start_time).getTime() / 1000;
    } else if (noteData.transcription_start_timestamp) {
      startTimeEpoch = noteData.transcription_start_timestamp;
    } else if (noteData.time_uploaded) {
      startTimeEpoch = noteData.time_uploaded;
    }

    // Calculate duration with fallback chain:
    // 1. Calculate from encounter start_time and end_time
    // 2. Use appointment duration (if encounter times not available)
    // 3. Fall back to transcription_length_seconds
    let durationSeconds: number | null = null;
    if (encounterData?.start_time && encounterData?.end_time) {
      const start = new Date(encounterData.start_time).getTime();
      const end = new Date(encounterData.end_time).getTime();
      durationSeconds = Math.floor((end - start) / 1000);
    } else if (encounterData?.appointment?.duration) {
      // Appointment duration is in minutes, convert to seconds
      durationSeconds = encounterData.appointment.duration * 60;
    } else if (noteData.transcription_length_seconds) {
      durationSeconds = noteData.transcription_length_seconds;
    }

    // Format header information using encounter-first fallback chain
    const headerInfo = {
      title: noteData.note_title ? `Note Title: ${noteData.note_title}` : undefined,
      provider: `Provider: ${(noteData.issuing_user as any)?.name || (noteData as any).issuing_user_name}`,
      date: `Date: ${startTimeEpoch ? formatDate(startTimeEpoch, 'datelong') : 'N/A'}`,
      time: `Time: ${
        startTimeEpoch
          ? durationSeconds
            ? `${formatDate(startTimeEpoch, 'time')} – ${formatDate(startTimeEpoch + durationSeconds, 'time')}`
            : formatDate(startTimeEpoch, 'time')
          : 'N/A'
      }`,
      duration: `Duration: ${durationSeconds ? formatDuration(durationSeconds) : 'N/A'}`,
    };

    const headerPlainText =
      `${headerInfo.title ? headerInfo.title + '\n' : ''}` +
      `${headerInfo.provider}\n` +
      `${headerInfo.date}\n` +
      `${headerInfo.time}\n` +
      `${headerInfo.duration}\n\n`;

    // Basic HTML formatting for the header
    const headerHtml =
      `${headerInfo.title ? `<p><strong>${headerInfo.title}</strong></p>` : ''}` +
      `<p><strong>${headerInfo.provider}</strong></p>` +
      `<p><strong>${headerInfo.date}</strong></p>` +
      `<p><strong>${headerInfo.time}</strong></p>` +
      `<p><strong>${headerInfo.duration}</strong></p><br/>`;

    // Add header if preference is enabled and copying multiple sections (either copyAll or multiple selected)
    const copyingMultiple = copyAll || selectedSections.length > 1;
    if (copyingMultiple && includeHeaderOnCopyMultiple) {
      plainTextString += headerPlainText;
      htmlString += headerHtml;
    }

    // Iterate over all sections in the note
    for (const [sectionIndex, section] of noteData.content.sections.entries()) {
      // Check if the section index is in the `selectedSections` array or if copyAll is true
      if (selectedSections.includes(sectionIndex) || copyAll === true) {
        // Clean section name by removing text between parentheses
        const cleanedName = (section.name as string).replace(/\s*\([^)]*\)/g, '');
        const sectionTitle = titleCase(cleanedName);

        // Add section header to both strings
        plainTextString += `===\n${sectionTitle}\n===\n\n`;
        // Use h2 for section titles in HTML, add <hr> for separation
        htmlString += htmlString ? `<hr/><h2>${sectionTitle}</h2>` : `<h2>${sectionTitle}</h2>`;

        const currentText = section.edited_text ? section.edited_text : section.text;

        if (section.markdown) {
          // For markdown sections, check if we have a BlockNote editor instance
          const blockNoteEditor = blockNoteEditorsRef.current.get(sectionIndex);

          if (blockNoteEditor) {
            // Use BlockNote's native HTML conversion for perfect consistency
            try {
              const sectionHtml = await blockNoteEditor.blocksToHTMLLossy(blockNoteEditor.document);
              const sectionMarkdown = await blockNoteEditor.blocksToMarkdownLossy(
                blockNoteEditor.document,
              );

              htmlString += sectionHtml; // Append BlockNote-generated HTML
              plainTextString += sectionMarkdown + '\n\n'; // Use markdown for plain text
            } catch (e) {
              console.error('Error converting BlockNote content:', e);
              // Fallback to marked if BlockNote conversion fails
              const sectionHtml = await marked(currentText);
              htmlString += sectionHtml;
              plainTextString +=
                removeMd(currentText, {
                  stripListLeaders: false,
                  useImgAltText: false,
                }) + '\n\n';
            }
          } else {
            // Fallback: Use marked if no BlockNote editor is available
            try {
              const sectionHtml = await marked(currentText); // Convert markdown to HTML
              htmlString += sectionHtml; // Append generated HTML directly
              plainTextString +=
                removeMd(currentText, {
                  stripListLeaders: false,
                  useImgAltText: false,
                }) + '\n\n'; // Strip markdown for plain text
            } catch (e) {
              console.error('Error parsing markdown:', e);
              // Fallback: treat as plain text if markdown parsing fails
              htmlString += `<div style="white-space: pre-wrap;">${currentText}</div>`;
              plainTextString += currentText + '\n\n';
            }
          }
        } else {
          // For plain text sections
          const textWithBreaks = currentText.replace(/\n/g, '<br/>');
          htmlString += `<div style="white-space: pre-wrap;">${textWithBreaks}</div>`; // Preserve newlines without using <pre>
          plainTextString += currentText + '\n\n';
        }

        htmlString += '<br/>'; // Add a break after each section's content in HTML

        if (section.module_id) {
          moduleIds.push(section.module_id);
        }
      }
    }

    // Use copyRichText utility
    const success = await copyRichText(htmlString, plainTextString, includeHtml);

    if (success) {
      // Create action-specific success messages
      let successMessage = '';
      if (copyAll) {
        successMessage = `All ${totalSections} section${totalSections === 1 ? '' : 's'} copied to clipboard!`;
      } else {
        const copiedCount = selectedSections.length;
        successMessage = `${copiedCount} selected section${copiedCount === 1 ? '' : 's'} copied to clipboard!`;
      }

      enqueueSnackbar(successMessage, {
        variant: 'success',
      });

      // Track note copy event (existing telemetry)
      if (!demoMode && noteId) {
        noteMetrics.copied(noteId, {
          copy_type: copyAll ? 'all' : 'selected',
          sections_count: totalSections,
          sections_copied: copyAll ? totalSections : selectedSections.length,
          include_header: copyingMultiple && includeHeaderOnCopyMultiple,
          include_html: includeHtml,
          source: 'button', // Currently only button-triggered copy is implemented
        });
      }

      // Track copy event (new telemetry model) - one event per section
      if (!demoMode && noteId) {
        const copiedSections = copyAll
          ? noteData.content.sections
          : noteData.content.sections.filter((_, idx) => selectedSections.includes(idx));

        const context: TelemetryContext = {
          docId: `note:${noteId}`,
          getDocVersion: () => '0',
        };

        copiedSections.forEach(section => {
          const sectionText = section.edited_text || section.text;
          buildSelectionCopied({
            context,
            lenSelected: sectionText.length,
            lenTotal: sectionText.length,
            modality: copyAll ? 'copy_all' : 'copy_selected',
            metadata: {
              note_id: noteId,
              section_id: section.section_id,
              module_id: section.module_id,
              section_title: section.name,
              encounter_id: encounterId,
            },
          });
        });
      }
    } else {
      enqueueSnackbar('Failed to copy sections.', {
        variant: 'error',
      });
    }

    if (demoMode) {
      return;
    }

    handleSendCopyEvents(moduleIds, copyAll);
    handleSetCopyStatus();
  };

  const handleGeneratePDF = async () => {
    if (demoMode) {
      return;
    }

    // Track PDF export
    noteMetrics.export(noteId || '', {
      export_type: 'pdf',
    });

    setPDFGenerating(true);
    const accessToken = await getAccessToken();

    const response = await APIService.makeAPIPostRequest({
      requestString: '/notes/generatePDF',
      accessToken: accessToken,
      body: {
        note_id: noteId,
      },
    });

    if (response.ok) {
      const data = response.value;

      // start polling
      const polling = setInterval(async () => {
        const statusResponse = await APIService.makeAPIGetRequest({
          requestString: `/notes/checkPDFStatus/${data.pdf_generation_session_id}`,
          accessToken: accessToken,
        });

        if (statusResponse.ok) {
          const statusData = statusResponse.value;
          if (statusData.status === 'completed') {
            clearInterval(polling);
            setPDFGenerating(false);
            // Open the PDF in a new tab
            window.open(statusData.download_url, '_blank');
          }
        }
      }, 3000); // polling every 3 seconds
    }
  };

  const handleRegenerateSection = async (
    moduleId: string,
    setAwaitingRegenerationTaskId: (taskId: string) => void,
  ) => {
    if (demoMode) {
      return;
    }

    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    const response = await APIService.makeAPIPostRequest({
      requestString: '/notes/regenerateSection',
      accessToken: accessToken,
      body: {
        note_id: noteId,
        module_id: moduleId,
      },
    });

    if (response.ok) {
      setAwaitingRegenerationTaskId(response.value.task_id);
    }
  };

  const handleRegenerateNoteWithNewTemplate = async () => {
    if (!templateIdToRegenerate) {
      return;
    }

    if (demoMode) {
      return;
    }

    setIsRegeneratingNote(true);
    const accessToken = await getAccessToken();

    if (!accessToken) {
      setIsRegeneratingNote(false);
      enqueueSnackbar('Authentication failed. Please try again.', {
        variant: 'error',
      });
      return;
    }

    try {
      const response = await APIService.makeAPIPostRequest({
        requestString: '/noteProcessingSession/retryNoteProcessingWithNewTemplate',
        accessToken: accessToken,
        body: {
          note_id: noteId,
          template_id: templateIdToRegenerate,
        },
      });

      if (response.ok) {
        const { note_id: newNoteId, thread_id } = response.value;

        if (!thread_id) {
          enqueueSnackbar('Note regeneration started successfully!', {
            variant: 'success',
          });
        }
        navigate(getNoteRoute({ note_id: newNoteId, thread_id }));
      } else {
        enqueueSnackbar('Failed to regenerate note. Please try again.', {
          variant: 'error',
        });
      }
    } catch (error) {
      enqueueSnackbar('An error occurred while regenerating the note.', {
        variant: 'error',
      });
    } finally {
      setIsRegeneratingNote(false);
    }
  };

  const handleRequestAIEdit = async (
    sectionText: string,
    editInstructions: string,
    moduleId: string,
    setAwaitingEditTaskId: (taskId: string) => void,
  ) => {
    const accessToken = await getAccessToken();

    if (!accessToken || !noteData) {
      return;
    }

    const url = '/notes/editSectionWithAI';
    const body = {
      section_content: sectionText,
      edit_instructions: editInstructions,
      module_id: moduleId,
      note_id: noteData.note_id,
    };

    try {
      const response = await APIService.makeAPIPostRequest({
        requestString: url,
        accessToken: accessToken,
        body: body,
      });

      if (response.ok) {
        setAwaitingEditTaskId(response.value.task_id);
      }
    } catch (error) {
      console.error('Failed to submit:', error);
    }
  };

  const fetchAdminEmails = async () => {
    if (demoMode) {
      return;
    }

    const accessToken = await getAccessToken();

    const response = await APIService.makeAPIGetRequest({
      requestString: '/user/getAdminEmails',
      accessToken: accessToken,
    });

    if (response.ok) {
      const data = response.value;
      setEmailList(data.admin_emails);
    }
  };

  const handleOpenModal = () => {
    fetchAdminEmails();
    setEmailModalOpen(true);

    // Track email modal opened
    noteMetrics.emailed(noteId || '', {
      action: 'email_modal_opened',
    });
  };

  const sendPDFAsEmail = async () => {
    if (sendPDFEmailList.length > 0) {
      setLoadingText('Sending');
      const accessToken = await getAccessToken();

      try {
        const response = await APIService.makeAPIPostRequest({
          requestString: '/email/sendPDFAsEmail',
          accessToken: accessToken,
          body: {
            note_id: noteId,
            email_list: sendPDFEmailList,
          },
        });

        if (response.ok) {
          const data = response.value;
          setSendPDFEmailList([]);
          setLoadingText('Sent');
          setTimeout(() => setLoadingText(''), 1000);
          setTimeout(() => setEmailModalOpen(false), 1000);
        } else {
          throw new Error('Failed to send PDF email.');
        }
      } catch (error) {
        console.error(error);
        setLoadingText('Sorry, could not send.');
        setTimeout(() => setLoadingText(''), 3000);
        setTimeout(() => setEmailModalOpen(false), 1000);
      }
    } else {
      setError('Please select at least one email.');
    }
  };

  const handleDeleteNote = async () => {
    if (demoMode) {
      setShowDeleteModal(false);
      return;
    }

    const accessToken = await getAccessToken();

    const response = await APIService.makeAPIPostRequest({
      requestString: '/notes/delete',
      accessToken: accessToken,
      body: { note_id: noteId },
    });

    if (response.ok) {
      const data = response.value;
      if (data.status === 200) {
        const win: Window = window;
        win.location = `/notes`;
      }
    } else {
      enqueueSnackbar(response.error?.message || 'Failed to delete note', {
        variant: 'error',
      });
    }

    setShowDeleteModal(false);
  };

  const handleArchiveNote = async () => {
    if (demoMode) {
      return;
    }

    const accessToken = await getAccessToken();

    const response = await APIService.makeAPIPostRequest({
      requestString: '/notes/updateNoteReviewStatus',
      accessToken: accessToken,
      body: {
        note_id: noteId,
        status: 'archived',
      },
    });

    if (response.ok) {
      const data = response.value;
      if (data.status === 200) {
        const win: Window = window;

        if (origin === 'group') {
          win.location = `/group`;
        } else {
          win.location = `/notes`;
        }
      }
    }
  };

  const handleReviewNote = async () => {
    if (demoMode) {
      return;
    }

    const accessToken = await getAccessToken();

    const response = await APIService.makeAPIPostRequest({
      requestString: '/notes/updateNoteCompletionStatus',
      accessToken: accessToken,
      body: {
        note_id: noteId,
        completion_status: 'reviewing',
      },
    });

    if (response.ok) {
      const data = response.value;
      if (data.status === 200) {
        window.location.reload();
      }
    }
  };

  const handleCompleteNote = async () => {
    if (demoMode) {
      return;
    }

    // Track note signing attempt (single event call with all metadata)
    const timeToSign = editStartTimeRef.current ? Date.now() - editStartTimeRef.current : undefined;

    noteMetrics.sign(noteId || '', {
      time_to_sign: timeToSign,
      edits_made: String(sectionEditCountRef.current),
      ...(timeToSign && { time_to_sign_ms: String(timeToSign) }),
    });

    // Check if the onboarding step is completed
    if (
      noteSigningEnabled &&
      !userState?.onboardingSteps?.note_signing_onboarding_completed
    ) {
      setIsOnboardingOpen(true);
      return;
    }

    setIsSubmitting(true);

    const accessToken = await getAccessToken();

    const response = await APIService.makeAPIPostRequest({
      requestString: '/notes/updateNoteCompletionStatus',
      accessToken: accessToken,
      body: {
        note_id: noteId,
        completion_status: 'completed',
      },
    });

    if (response.ok) {
      const data = response.value;
      if (data.status === 200) {
        window.location.reload();
      }
    } else {
      enqueueSnackbar('Failed to complete note', {
        variant: 'error',
      });
      setIsSubmitting(false);
    }
  };

  const handleSubmitSectionRatingFeedback = async (
    section_id: string,
    rating: number,
    module_id: string,
  ) => {
    if (!section_id) {
      return;
    }
    if (demoMode) {
      return;
    }

    const accessToken = await getAccessToken();

    const response = await APIService.makeAPIPostRequest({
      requestString: '/feedback/addNoteSectionFeedbackEvent',
      accessToken: accessToken,
      body: {
        note_id: noteId,
        section_id: section_id,
        section_rating: rating,
        module_id: module_id,
      },
    });

    if (response.ok) {
      const data = response.value;
    }
  };

  const handleSubmitSectionCommentFeedback = async (
    section_id: string,
    comment: string,
    module_id: string,
    ai_instruction_session_id: string,
  ) => {
    if (!section_id) {
      return;
    }
    if (demoMode) {
      return;
    }

    const accessToken = await getAccessToken();

    const response = await APIService.makeAPIPostRequest({
      requestString: '/feedback/addNoteSectionFeedbackEvent',
      accessToken: accessToken,
      body: {
        note_id: noteId,
        section_id: section_id,
        section_comment: comment,
        module_id: module_id,
        ai_instruction_session_id: ai_instruction_session_id,
      },
    });

    if (response.ok) {
      const data = response.value;
    }
  };

  const handleSubmitNoteCommentFeedback = async (comment: string) => {
    if (demoMode) {
      return;
    }

    const accessToken = await getAccessToken();

    const response = await APIService.makeAPIPostRequest({
      requestString: '/feedback/addNoteFeedbackEvent',
      accessToken: accessToken,
      body: {
        note_id: noteId,
        note_comment: comment,
      },
    });

    if (response.ok) {
      const data = response.value;
    }
  };

  const handleSubmitNoteRatingFeedback = async (rating: number) => {
    if (demoMode) {
      return;
    }

    const accessToken = await getAccessToken();

    const response = await APIService.makeAPIPostRequest({
      requestString: '/feedback/addNoteFeedbackEvent',
      accessToken: accessToken,
      body: {
        note_id: noteId,
        note_rating: rating,
      },
    });

    if (response.ok) {
      const data = response.value;
    }
  };

  const handleCloseNoteOnboarding = () => {
    setShowNoteOnboarding(false);
  };

  const handleWordClick = (word: string) => {
    if (!troubleWords || !noteData) {
      console.error('troubleWords or noteData is not defined');
      return;
    }

    // Find the trouble word entry for this word
    const troubleWordEntry = troubleWords.find(
      entry =>
        entry.correct_spelling.toLowerCase() === word.toLowerCase() ||
        entry.misspellings.some(misspelling => misspelling.toLowerCase() === word.toLowerCase()),
    );

    if (troubleWordEntry) {
      const correctSpelling = troubleWordEntry.correct_spelling;

      // Determine if the clicked word is a misspelling or the correct spelling
      const isMisspelling = troubleWordEntry.misspellings.some(
        misspelling => misspelling.toLowerCase() === word.toLowerCase(),
      );

      // Open Find/Replace modal with pre-filled values
      if (isMisspelling) {
        // If it's a misspelling, set up to replace it with correct spelling
        openModal(MODAL_TYPES.FIX_SPELLING, {
          sections: noteData.content.sections,
          prefillFind: word,
          prefillReplace: correctSpelling,
          analyticsMetadata: {
            note_id: noteId,
            source: 'misspelling',
            prefilled_term: true,
            encounter_id: encounterId,
          },
        });
      } else {
        // If it's the correct spelling, just open the modal with it in the find field
        openModal(MODAL_TYPES.FIX_SPELLING, {
          sections: noteData.content.sections,
          prefillFind: word,
          analyticsMetadata: {
            note_id: noteId,
            source: 'misspelling',
            prefilled_term: true,
            encounter_id: encounterId,
          },
        });
      }
    }

    setSelectedWord(word);
  };

  const formatMatchedWords = (words: string[]) => {
    return words.map((word, index) => {
      // Get the first misspelling for this correct word from the map
      const misspellings = misspellingMap[word] || [];
      const firstMisspelling = misspellings[0];

      return (
        <React.Fragment key={index}>
          {index > 0 && (index === words.length - 1 ? ' and ' : ', ')}
          <span
            style={{
              color: '#36229E',
              cursor: 'pointer',
              fontWeight: 'bold',
              textDecoration: 'underline',
            }}
            onClick={() => {
              if (!noteData) return;
              // If we have a misspelling, open modal to replace it with correct spelling
              if (firstMisspelling) {
                openModal(MODAL_TYPES.FIX_SPELLING, {
                  sections: noteData.content.sections,
                  prefillFind: firstMisspelling,
                  prefillReplace: word,
                  analyticsMetadata: {
                    note_id: noteId,
                    source: 'misspelling',
                    prefilled_term: true,
                    encounter_id: encounterId,
                  },
                });
              } else {
                // Fallback to just searching for the word
                openModal(MODAL_TYPES.FIX_SPELLING, {
                  sections: noteData.content.sections,
                  prefillFind: word,
                  analyticsMetadata: {
                    note_id: noteId,
                    source: 'misspelling',
                    prefilled_term: true,
                    encounter_id: encounterId,
                  },
                });
              }
            }}
            title={
              firstMisspelling
                ? `Click to replace "${firstMisspelling}" with "${word}"`
                : `Click to find and replace "${word}"`
            }
          >
            {word}
          </span>
        </React.Fragment>
      );
    });
  };

  const handleAssign = (patient: Patient) => {
    // Optimistically update cache with assigned patient
    if (!demoMode) {
      dispatch(
        notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId: noteId! }, draft => {
          draft.patient = patient as any; // Patient type compatible with PatientInfo
          draft.patient_id = patient.patient_id;
        }),
      );
      // Note: No immediate invalidation - only refetch on error via handleAssignError
    }
  };
  const handleUnassign = () => {
    // Optimistically update cache - patient is now null
    if (!demoMode) {
      dispatch(
        notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId: noteId! }, draft => {
          draft.patient = null;
          draft.patient_id = null;
        }),
      );
      // Note: No immediate invalidation - only refetch on error via handleUnassignError
    }
  };

  const handleAssignError = () => {
    // Invalidate cache to refetch correct data from server (rollback optimistic update)
    if (!demoMode) {
      dispatch(notesApi.util.invalidateTags([{ type: 'Note', id: noteId }]));
    }
  };

  const handleUnassignError = () => {
    // Invalidate cache to refetch correct data from server (rollback optimistic update)
    if (!demoMode) {
      dispatch(notesApi.util.invalidateTags([{ type: 'Note', id: noteId }]));
    }
  };

  const handleModuleCustomizationSession = (sectionIndex: number, userFeedback?: string) => {
    if (!noteData) return;
    const section = noteData.content.sections[sectionIndex];

    if (!section.module_id) {
      return;
    }

    setModuleCustomizationModalInfo(prevState => ({
      ...prevState,
      userFeedback: userFeedback,
      showModal: true,
      module_id: section.module_id,
      note_id: noteId || '',
      initialComparisonText: section.edited_text ? section.edited_text : section.text,
      section_name: section.name,
      sectionIndex: sectionIndex,
    }));
  };

  const getRelevantMisspellings = (words: string[]) => {
    if (!troubleWords) {
      console.error('troubleWords is not defined');
      return {};
    }

    const relevantMisspellings: { [key: string]: string[] } = {};
    words.forEach(word => {
      const lowerCaseWord = word.toLowerCase();
      const matchingEntry = troubleWords.find(
        entry =>
          entry.correct_spelling.toLowerCase() === lowerCaseWord ||
          entry.misspellings.some(misspelling => misspelling.toLowerCase() === lowerCaseWord),
      );

      if (matchingEntry) {
        relevantMisspellings[matchingEntry.correct_spelling] = matchingEntry.misspellings;
      }
    });

    return relevantMisspellings;
  };

  const highlightMisspelledWords = (
    text: string,
    misspelledWords: string[],
    onClick: (word: string) => void,
  ) => {
    let highlightedText = text;

    misspelledWords.forEach(word => {
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      highlightedText = highlightedText.replace(regex, match => {
        return `<span class="highlight" onclick="openSpellModal('${match}')">${match}</span>`;
      });
    });

    return highlightedText;
  };

  const handleHighlightClick = (word: string) => {
    setSelectedWord(word);
  };

  useEffect(() => {
    if (scrollRef.current && activeSection) {
      // Find the associated content section in the main container
      const contentSection = scrollRef.current.querySelector(`#section-${activeSection.id}`);

      // Scroll the main container to the content section
      contentSection?.scrollIntoView({ behavior: 'smooth', inline: 'start' });
    }
  }, [activeSection]);

  // Helper function to escape special regex characters
  function escapeRegExp(string: string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  const handleResubmitClaim = async () => {
    if (demoMode) {
      return;
    }

    const accessToken = await getAccessToken();

    const response = await APIService.makeAPIPostRequest({
      requestString: '/billing/resubmitClaim',
      accessToken: accessToken,
      body: { note_id: noteId },
    });

    if (response.ok) {
      setBillingSubmitted(false); // Assuming this state controls the submit button visibility
      console.log('Claim resubmit allowed');
    } else {
      // Handle error
      console.error('Failed to resubmit claim');
      throw new Error('Failed to resubmit claim'); // Throw an error to indicate failure
    }
  };

  const handleDocumentSelect = (docType: string, docId: string, isJotdoc?: boolean) => {
    console.log('NoteViewRevamp: handleDocumentSelect called', {
      docType,
      docId,
      isJotdoc,
      noteId,
      encounterId,
    });
    if (!noteId) {
      console.warn('NoteViewRevamp: No noteId, cannot navigate');
      return;
    }
    if (!docId) {
      console.warn('NoteViewRevamp: No docId provided');
      return;
    }

    // Check if it's a jotdoc using the flag (more reliable than type name)
    if (isJotdoc && docId && docId !== noteId) {
      console.log('NoteViewRevamp: Navigating to jotdoc route', `/jotdoc/${docId}`);
      // Navigate to jotdoc route
      navigate(`/jotdoc/${docId}`, {
        state: { noteId, encounterId },
      });
    } else {
      console.log('NoteViewRevamp: Navigating to note route or staying');
      // Navigate to the selected note (or stay if already there)
      if (docId === noteId) {
        setSelectedDocId(noteId);
        setViewMode({ mode: 'note' });
      } else {
        // Navigate to the different note using docId (not noteId)
        navigate(`/note/${docId}`, {
          state: { encounterId },
        });
      }
    }
  };

  // Fetch encounter data for polling (when waiting for additional outputs)
  // Uses RTK Query cache invalidation to trigger refetch
  const fetchEncounterData = useCallback(async () => {
    if (!encounterId || demoMode) {
      console.log(
        '[NoteViewRevamp] fetchEncounterData: Skipping - encounterId:',
        encounterId,
        'demoMode:',
        demoMode,
      );
      return;
    }

    console.log(
      '[NoteViewRevamp] fetchEncounterData: Refreshing encounter',
      encounterId,
      new Date().toISOString(),
    );
    // Invalidate RTK Query cache for encounter, which will trigger a refetch
    dispatch(encountersApi.util.invalidateTags([{ type: 'Encounter', id: encounterId }]));
  }, [encounterId, demoMode, dispatch]);

  useEffect(() => {
    if (!noteData?.content?.sections) {
      return;
    }

    const misspelledMatches: string[] = [];
    const correctSpellingMatches: string[] = [];
    const spellingMap: { [correctSpelling: string]: string[] } = {};

    if (!troubleWords) {
      console.warn('troubleWords is not defined');
      return;
    }

    noteData.content.sections.forEach(section => {
      const text = section.text;

      troubleWords.forEach(troubleWord => {
        const correctSpelling = troubleWord.correct_spelling;
        troubleWord.misspellings.forEach((misspelling: string) => {
          // Use lookahead and lookbehind to match words bounded by spaces, punctuation, or start/end of string
          // Updated regex to include single and double quotes
          const regex = new RegExp(
            `(?<=^|\\s|[.,!?;:()\\[\\]\\-/'"])(${escapeRegExp(
              misspelling,
            )})(?=\\s|[.,!?;:()\\[\\]\\-/'"]|$)`,
            'gi',
          );
          const matches = text.match(regex);
          if (matches && !misspelledMatches.includes(misspelling.toLowerCase())) {
            misspelledMatches.push(misspelling.toLowerCase());
            if (!correctSpellingMatches.includes(correctSpelling)) {
              correctSpellingMatches.push(correctSpelling);
              spellingMap[correctSpelling] = [];
            }
            // Add this misspelling to the map for this correct spelling
            if (!spellingMap[correctSpelling].includes(misspelling)) {
              spellingMap[correctSpelling].push(misspelling);
            }
          }
        });
      });
    });

    setMatchedMisspelled(misspelledMatches);
    setMatchedWords(correctSpellingMatches);
    setMisspellingMap(spellingMap);
  }, [noteData, troubleWords]);

  useEffect(() => {
    if (userState?.onboardingSteps && !demoMode) {
      if (!userState.onboardingSteps.viewed_first_note) {
        updateOnboardingStep('viewed_first_note', true);
      }
    }
  }, [userState]);

  useEffect(() => {
    if (loadedInChromeExtension) {
      if (
        !localStorage.getItem('viewed_chrome_extension_tooltips') ||
        localStorage.getItem('viewed_chrome_extension_tooltips') !== 'true'
      ) {
        localStorage.setItem('viewed_chrome_extension_tooltips', 'true');
        setShowChromeExtensionTooltips(true);
      } else {
        setShowChromeExtensionTooltips(false);
      }
    }
  }, [loadedInChromeExtension]);

  // NOTE: Patient and encounter data now comes directly from the note data
  // For production: v2Note includes patient and encounter
  // For demo mode: demoNoteData includes patient and encounter
  // No separate API calls needed for initial load!
  // fetchEncounterData is only used for polling when waiting for additional encounter outputs

  // Show toolbar and update context with patient ID and note ID
  useEffect(() => {
    showToolbar();
    setToolbarContext({
      patientId: noteData?.patient_id,
      noteId: noteId,
      source: 'note',
    });

    // Cleanup: Hide toolbar and clear context when component unmounts
    return () => {
      hideToolbar();
      setToolbarContext(undefined);
    };
  }, [noteData?.patient_id, noteId, setToolbarContext, showToolbar, hideToolbar]);

  useEffect(() => {
    if (patientData) {
      const eligibilityRequiredFields = ['first_name', 'last_name', 'payer_id'];
      const billingRequiredFields = [
        'first_name',
        'last_name',
        'dob',
        'address',
        'city',
        'state',
        'zip',
        'gender',
        'payer_id',
        'payer_name',
        'payer_relationship',
        'insurance_policy_number',
      ];
      const missingEligibilityFields = eligibilityRequiredFields.filter(
        field => !patientData[field as keyof Patient],
      );
      const missingBillingFields = billingRequiredFields.filter(
        field => !patientData[field as keyof Patient],
      );
      setMissingPatientEligibilityFields(missingEligibilityFields);
      setMissingPatientBillingFields(missingBillingFields);
    }
  }, [patientData]);

  useEffect(() => {
    const eligibilityRequiredProviderFields = ['npi_number', 'tax_id'];
    const billingRequiredProviderFields = [
      'npi_number',
      'first_name',
      'last_name',
      'facility_address',
      'state',
      'city',
      'zip',
      'phone',
      'tax_id',
      'tax_id_type',
      'accept_insurance_payment_as_full',
    ];
    const missingEligibilityFields = eligibilityRequiredProviderFields.filter(
      field =>
        !userState?.providerBillingInfo?.[field as keyof typeof userState.providerBillingInfo],
    );
    const missingBillingFields = billingRequiredProviderFields.filter(
      field =>
        !userState?.providerBillingInfo?.[field as keyof typeof userState.providerBillingInfo],
    );
    setMissingProviderEligibilityFields(missingEligibilityFields);
    setMissingProviderBillingFields(missingBillingFields);
  }, [userState?.providerBillingInfo]);

  // Reset polling expired state when encounterId changes or polling conditions change
  useEffect(() => {
    if (encounterPollingEnabled && hasEncounterPollingExpired) {
      setHasEncounterPollingExpired(false);
    }
    // Reset expired state when outputs count becomes exactly 1 or when pending notes appear
    if ((encounterOutputsCount === 1 || hasPendingNotes) && hasEncounterPollingExpired) {
      setHasEncounterPollingExpired(false);
    }
  }, [encounterPollingEnabled, hasEncounterPollingExpired, encounterOutputsCount, hasPendingNotes]);

  // Debug logging for polling condition breakdown
  useEffect(() => {
    console.log('[NoteViewRevamp] Polling condition check', {
      isAuditEnabled,
      hasNoteId: Boolean(noteId),
      noteId,
      encounterId,
      encounterOutputsCount,
      'encounterOutputsCount === 1': encounterOutputsCount === 1,
      hasPendingNotes,
      '(encounterOutputsCount === 1 || hasPendingNotes)':
        encounterOutputsCount === 1 || hasPendingNotes,
      hasEncounterPollingExpired,
      shouldPollEncounterDocuments,
      outputs: encounterData?.outputs?.map((o: any) => ({
        type: o.type,
        status: o.status,
        encounter_document_id: o.encounter_document_id,
        jotdoc_id: o.jotdoc_id,
      })),
    });
  }, [
    isAuditEnabled,
    noteId,
    encounterId,
    hasEncounterPollingExpired,
    shouldPollEncounterDocuments,
    encounterOutputsCount,
    hasPendingNotes,
    encounterData?.outputs,
  ]);

  useEffect(() => {
    if (!shouldPollEncounterDocuments || !encounterId) {
      console.log('[NoteViewRevamp] Encounter polling: Not enabled', {
        shouldPollEncounterDocuments,
        encounterId,
        encounterOutputsCount,
        'encounterOutputsCount === 1': encounterOutputsCount === 1,
        hasPendingNotes,
        '(encounterOutputsCount === 1 || hasPendingNotes)':
          encounterOutputsCount === 1 || hasPendingNotes,
        hasEncounterPollingExpired,
        isAuditEnabled,
      });
      return;
    }

    console.log('[NoteViewRevamp] Encounter polling: Starting', {
      encounterId,
      encounterOutputsCount,
      hasPendingNotes,
      timestamp: new Date().toISOString(),
    });

    fetchEncounterData();

    const intervalId = window.setInterval(() => {
      console.log('[NoteViewRevamp] Encounter polling: Interval tick', new Date().toISOString());
      fetchEncounterData();
    }, 10000);

    const timeoutId = window.setTimeout(
      () => {
        console.log(
          '[NoteViewRevamp] Encounter polling: Timeout expired after 5 minutes',
          new Date().toISOString(),
        );
        setHasEncounterPollingExpired(true);
      },
      5 * 60 * 1000,
    );

    return () => {
      console.log('[NoteViewRevamp] Encounter polling: Cleaning up', new Date().toISOString());
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [
    shouldPollEncounterDocuments,
    fetchEncounterData,
    encounterId,
    encounterOutputsCount,
    hasPendingNotes,
    hasEncounterPollingExpired,
  ]);

  const fieldNameMapping = {
    // Patient fields
    first_name: 'First Name',
    last_name: 'Last Name',
    identifier: 'Patient ID',
    dob: 'Date of Birth',
    address: 'Address',
    phone: 'Phone Number',
    gender: 'Gender',

    // Provider fields
    npi_number: 'NPI Number',
    facility_address1: 'Facility Address',
    facility_address2: 'Facility Address 2',
    state: 'State',
    // place_of_service: "Place of Service",
  };

  // Helper function to get user-friendly field names
  const getUserFriendlyFieldName = (fieldName: string): string => {
    return fieldNameMapping[fieldName as keyof typeof fieldNameMapping] || fieldName;
  };

  const billingCodeExtra = noteData?.content?.extras?.find(extra => extra.name === 'billing_code');

  const predictedCodesExtra = noteData?.content?.extras?.find(
    extra => extra.name === 'billing_data_prediction',
  );

  // New function to render NoteSection or MarkdownNoteSection
  const renderNoteSectionComponent = ({
    section,
    sectionIndex,
    startingTutorialStep,
  }: {
    section: NoteSectionType;
    sectionIndex: number;
    startingTutorialStep?: number;
  }) => {
    // Find misspellings in this section's text
    const sectionText = (section.edited_text || section.text).toLowerCase();
    const sectionMisspellings: string[] = [];

    if (troubleWords) {
      troubleWords.forEach(troubleWord => {
        troubleWord.misspellings.forEach(misspelling => {
          const regex = new RegExp(`\\b${misspelling}\\b`, 'gi');
          if (regex.test(sectionText)) {
            sectionMisspellings.push(misspelling);
          }
        });
        // Also include correct spellings that are in the text (for highlighting)
        const correctRegex = new RegExp(`\\b${troubleWord.correct_spelling}\\b`, 'gi');
        if (correctRegex.test(sectionText)) {
          sectionMisspellings.push(troubleWord.correct_spelling);
        }
      });
    }

    const commonProps = {
      onWordClick: handleWordClick,
      misspelledWords: sectionMisspellings,
      heading: section.name,
      text: section.edited_text ? section.edited_text : section.text,
      sectionId: section.section_id,
      moduleId: section.module_id,
      isEdited: section.edited_text ? true : false,
      isAdded: selectedSections.includes(sectionIndex),
      handleAdd: () => handleAddSection(sectionIndex),
      isRated: section.is_rated ? true : false,
      isErrored: section.error ? true : false,
      context: section.context,
      handleSaveEdits: (editedText: string) => {
        handleSaveEdits(sectionIndex, editedText);
      },
      handleRevertEdits: () => {
        handleRevertEdits(sectionIndex);
      },
      isSaving: saving.includes(sectionIndex),
      isEditable: noteData?.completion_status !== 'completed',
      handleRegenerateSection: handleRegenerateSection,
      handleRequestAIEdit: handleRequestAIEdit,
      handleSectionRatingFeedback: (rating: number) =>
        handleSubmitSectionRatingFeedback(section.section_id, rating, section.module_id),
      handleSectionCommentFeedback: (comment: string, ai_instruction_session_id: string) => {
        handleSubmitSectionCommentFeedback(
          section.section_id,
          comment,
          section.module_id,
          ai_instruction_session_id,
        );
      },
      handleSectionSetCopyStatus: () => handleSetCopyStatus(),
      handleSectionSendCopyEvents: (module_id: string) => handleSendCopyEvents([module_id], false),
      handleStartModuleCustomizationSession: (userFeedback?: string) => {
        handleModuleCustomizationSession(sectionIndex, userFeedback);
      },
      setTutorialText: (headerText: string, description: string) => {
        setTutorialHeaderText(headerText);
        setTutorialDescription(description);
      },
      showChromeExtensionTooltips: showChromeExtensionTooltips,
      startingTutorialStep: startingTutorialStep,
      encounterId: encounterId,
      onAppendText: async (sectionId: string, newContent: string) => {
        // Use handleSaveEdits to save the new content
        handleSaveEdits(sectionIndex, newContent);
      },
      enableBracketHighlight: true,
    };

    return (
      <NoteSection
        {...commonProps}
        markdownMode={section.markdown}
        onEditorReady={
          section.markdown
            ? editor => {
                // Store the BlockNote editor instance for this section
                blockNoteEditorsRef.current.set(sectionIndex, editor);
              }
            : undefined
        }
      />
    );
  };

  // Handle loading state
  if (isLoading && !demoMode) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Handle error state
  if (isError && !demoMode) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="h6" color="error">
          Failed to load note
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {(queryError as any)?.message || 'An unknown error occurred'}
        </Typography>
      </Box>
    );
  }

  // Add safety check for noteData
  if (!noteData || !noteData.content) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <TutorialProvider
      tutorialKey="note-view"
      tutorialVersion={1}
      totalSteps={8}
      promptTitle={'JotPsych Encounter Note'}
      promptDescription={'Want to learn how to navigate and use the JotPsych encounter note?'}
    >
      <>
        <AssignPatientToNoteDialog
          open={openAssignPatientToNoteDialog}
          onClose={() => setOpenAssignPatientToNoteDialog(false)}
          noteId={noteId || ''}
          onAssign={handleAssign}
          onAssignError={handleAssignError}
        />
        <EmailNoteModal
          emailModalOpen={emailModalOpen}
          setEmailModalOpen={setEmailModalOpen}
          sendPDFAsEmail={sendPDFAsEmail}
          setSendPDFEmailList={setSendPDFEmailList}
          loadingText={loadingText}
          error={error}
          setError={setError}
        />
        <DeleteModal
          title="Delete Note?"
          isOpen={showDeleteModal}
          continueText="Delete"
          cancelText="Cancel"
          onCancel={() => {
            setShowDeleteModal(false);
          }}
          onContinue={handleDeleteNote}
        >
          Are you sure you want to delete this note? This action cannot be undone.
        </DeleteModal>
        <Modal open={moduleCustomizationModalInfo.showModal}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              outline: 'none',
              borderRadius: 8,
              width: '100%',
              maxWidth: '600px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, textAlign: 'center' }}>
              Teaching Temporarily Disabled
            </Typography>
            <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary' }}>
              Teaching has been temporarily disabled. Please get in touch with{' '}
              <Box component="a" href="mailto:contact@jotpsych.com" sx={{ color: 'primary.main' }}>
                contact@jotpsych.com
              </Box>{' '}
              for further questions.
            </Typography>
            <RoundedButton
              variant="contained"
              onClick={() => {
                setModuleCustomizationModalInfo(prevState => ({
                  ...prevState,
                  showModal: false,
                }));
              }}
              sx={{
                mt: 2,
              }}
            >
              Close
            </RoundedButton>
          </Box>
        </Modal>
        <SlideSidePane
          open={isBillingSidePaneOpen}
          onClose={handleCloseBillingSidePane}
          title="Submit Billing"
        >
          <BillingSubmissionView
            billingCodeExtra={billingCodeExtra?.data}
            providerBillingInfo={userState?.providerBillingInfo ?? undefined}
            patient={patientData}
            note={noteData}
            noteId={noteId}
            missingPatientBillingFields={missingPatientBillingFields}
            missingProviderBillingFields={missingProviderBillingFields}
            onBillingSubmitted={handleBillingSubmitted}
            onBillingCodeUpdate={(newBillingCode: string) => {
              return Promise.resolve();
            }}
          />
        </SlideSidePane>
        <SlideSidePane
          open={isPatientInformationSidePaneOpen}
          onClose={handleClosePatientInformationSidePane}
          title="Patient Information"
        >
          <PatientInformationView
            patientId={patientData?.patient_id!}
            highlightedFields={missingPatientBillingFields}
            onPatientSave={() => {
              // Refetch the note to get updated patient data (only in production mode)
              if (!demoMode) {
                refetchNote();
              }
              handleClosePatientInformationSidePane();
            }}
          />
        </SlideSidePane>
        <SlideSidePane
          open={isPatientEligibilitySidePaneOpen}
          onClose={handleClosePatientEligibilitySidePane}
          title="Patient Eligibility"
        >
          <PatientEligibilityView
            onPatientEligibilitySave={handlePatientEligibilitySave}
            initialPatRel={patRel}
            billingCodeInfo={billingCodeExtra?.data.billing_code}
            providerBillingInfo={userState?.providerBillingInfo ?? undefined}
            patient={patientData}
            note={noteData}
          />
        </SlideSidePane>
        <LayoutWrapper>
          <LayoutWrapper.TopBar>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
                minWidth: 'max-content',
              }}
            >
              <Box
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  flexDirection: 'row',
                  alignItems: 'center',
                  ml: { xs: 3, md: 4 },
                  order: 0,
                }}
              >
                <Typography
                  variant="body2"
                  onClick={() =>
                    setUIState({
                      ...uiState,
                      sidebar: { isHidden: !uiState.sidebar.isHidden },
                    })
                  }
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    '&:hover': {
                      cursor: 'pointer',
                    },
                  }}
                >
                  {uiState.sidebar.isHidden ? (
                    <>
                      <MenuIcon />
                      <KeyboardArrowRightOutlined />
                    </>
                  ) : (
                    <>
                      <MenuIcon />
                      <KeyboardArrowLeftOutlined />
                    </>
                  )}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  ml: { xs: 0, md: 4 },
                  order: { xs: 2, md: 1 },
                  pr: { xs: 3, sm: 4 },
                }}
              >
                {added.length > 0 && (
                  <RoundedButton
                    variant="outlined"
                    sx={{
                      fontSize: '0.85rem',
                      bgcolor: 'backgroundColors.secondary',
                      borderColor: 'borderColors.lightprimary',
                      border: '0.1px solid',
                      fontWeight: '700',
                      px: 2,
                    }}
                    onClick={() => {
                      setAdded([]);
                      setSelectedSections([]);
                    }}
                  >
                    Deselect All
                  </RoundedButton>
                )}

                <RoundedButton
                  variant="outlined"
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: '700',
                    whiteSpace: 'nowrap',
                    bgcolor: 'backgroundColors.secondary',
                    borderColor: 'borderColors.lightprimary',
                    border: '0.1px solid',
                    px: 2,
                  }}
                  onClick={() =>
                    openModal(MODAL_TYPES.FIX_SPELLING, {
                      sections: noteData.content.sections,
                      analyticsMetadata: {
                        note_id: noteId,
                        source: 'button',
                        encounter_id: encounterId,
                      },
                    })
                  }
                >
                  <SpellcheckIcon
                    sx={{
                      color: theme.palette.primary.light,
                      fontSize: '1rem',
                      paddingRight: 0.25,
                    }}
                  />
                  Find/replace
                </RoundedButton>

                {added.length > 0 && (
                  <RoundedButton
                    variant="outlined"
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: '700',
                      whiteSpace: 'nowrap',
                      px: 2,
                      bgcolor: 'backgroundColors.secondary',
                      borderColor: 'borderColors.lightprimary',
                      border: '0.1px solid',
                    }}
                    onClick={() => handleCopySections(false)}
                  >
                    <ContentCopyIcon
                      sx={{
                        color: theme.palette.primary.light,
                        fontSize: '1rem',
                        paddingRight: 0.25,
                      }}
                    />
                    Copy Selected Sections ({added.length})
                  </RoundedButton>
                )}

                <RoundedButton
                  variant="outlined"
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: '700',
                    whiteSpace: 'nowrap',
                    px: 2,
                    bgcolor: 'backgroundColors.secondary',
                    borderColor: 'borderColors.lightprimary',
                    border: '0.1px solid',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                  onClick={() => handleCopySections(true)}
                >
                  <ContentCopyIcon
                    sx={{
                      color: theme.palette.primary.light,
                      fontSize: '1rem',
                    }}
                  />
                  Copy All Sections
                  <Box
                    component="span"
                    onClick={handleOpenCopyPasteSettings}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      ml: 'auto',
                      pl: 1,
                      borderLeft: '1px solid',
                      borderLeftColor: 'divider',
                      '&:hover': {
                        opacity: 0.7,
                      },
                    }}
                  >
                    <SettingsIcon sx={{ fontSize: '0.9rem', color: theme.palette.primary.light }} />
                  </Box>
                </RoundedButton>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  mr: { xs: 0, md: 4 },
                  order: { xs: 1, md: 2 },
                }}
              >
                <RoundedButton
                  variant="outlined"
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: '700',
                    whiteSpace: 'nowrap',
                    px: 2,
                    bgcolor: 'backgroundColors.secondary',
                    borderColor: 'borderColors.lightprimary',
                    border: '0.1px solid',
                  }}
                  onClick={() => {
                    const previousMode = viewMode.mode;
                    const timeInPreviousView = Date.now() - viewModeTimestampRef.current.timestamp;

                    if (viewMode.mode === 'transcript') {
                      // Track switching to Note view
                      if (noteId) {
                        noteMetrics.viewNoteClicked(noteId, {
                          previous_view: previousMode,
                          time_in_previous_view_ms: timeInPreviousView,
                          encounter_id: encounterId,
                        });
                      }

                      setSelectedDocId(noteId);
                      setViewMode({ mode: 'note' });

                      // Update timestamp ref
                      viewModeTimestampRef.current = {
                        timestamp: Date.now(),
                        mode: 'note',
                      };
                    } else {
                      // Track switching to Transcript view
                      if (noteId) {
                        noteMetrics.viewTranscriptClicked(noteId, {
                          previous_view: previousMode,
                          time_in_previous_view_ms: timeInPreviousView,
                          encounter_id: encounterId,
                        });
                      }

                      setViewMode({ mode: 'transcript' });

                      // Update timestamp ref
                      viewModeTimestampRef.current = {
                        timestamp: Date.now(),
                        mode: 'transcript',
                      };
                    }
                  }}
                >
                  {viewMode.mode === 'transcript' ? 'View Note' : 'View Transcript'}
                </RoundedButton>
                <SwitchTemplateButton
                  noteId={noteId}
                  demoMode={demoMode}
                  currentTemplateName={getTemplateName(noteData)}
                  currentTemplateId={getTemplateId(noteData)}
                  encounterId={encounterId}
                />
                {/* Edit Template button - requires both feature flag and note-specific permission */}
                {showEditTemplate && noteData?.can_edit_template === true && noteData.template && (
                  <RoundedButton
                    variant="outlined"
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: '700',
                      whiteSpace: 'nowrap',
                      px: 2,
                      bgcolor: 'backgroundColors.secondary',
                      borderColor: 'borderColors.lightprimary',
                      border: '0.1px solid',
                    }}
                    onClick={() => {
                      // Track intent to edit template
                      const templateInfo = extractTemplateInfo(noteData);
                      noteMetrics.templateEditClicked(noteId || '', {
                        template_id: templateInfo?.id,
                        template_name: templateInfo?.name,
                        source: 'note_header',
                        section_count: noteData?.content?.sections?.length || 0,
                      });

                      // Navigate to templates page with template ID to open editor
                      if (templateInfo?.id) {
                        navigate(`/templates?id=${templateInfo.id}`);
                      } else {
                        navigate('/templates');
                      }
                    }}
                  >
                    {editButtonLabel}
                  </RoundedButton>
                )}
              </Box>
            </Box>
          </LayoutWrapper.TopBar>
          <LayoutWrapper.LeftSidebar>
            <Box className="flex-1" sx={removeScrollBar}>
              <TutorialStep
                step={2}
                description="You can use the navigation sidebar to quickly jump to a particular section of the note."
                placement={'right'}
              >
                {encounterData &&
                  Array.isArray(encounterData.outputs) &&
                  encounterData.outputs.length > 0 && (
                    <Box>
                      <NoteDocumentPicker
                        selectedDocId={selectedDocId}
                        onSelectDocument={(docType, docId, isJotdoc) => {
                          handleDocumentSelect(docType, docId, isJotdoc);
                        }}
                        encounterData={encounterData as any} // Type assertion to bypass type checking temporarily
                        parentNoteId={noteId}
                        extras={noteData?.content?.extras}
                      />
                    </Box>
                  )}
                <Typography
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    mb: 2,
                    color: 'textColors.primaryText',
                  }}
                >
                  Note Sections
                </Typography>
                <ul>
                  {noteData.content &&
                    noteData.content.sections.map((section, sectionIndex) => {
                      const isSelected = selectedSections.includes(sectionIndex);
                      return (
                        <ListItem key={sectionIndex} sx={{ width: '100%' }}>
                          <Box
                            onClick={() => handleSectionClick(sectionIndex)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              width: '100%',
                              px: 1,
                              fontSize: 'text-base',
                              color: 'textColors.darkHeader',
                              borderRadius: 'lg',
                              '&:hover': {
                                cursor: 'pointer',
                              },
                            }}
                          >
                            <Box onClick={event => handleSidebarIconClick(event, sectionIndex)}>
                              <SVGIcon name={isSelected ? 'check-filled' : 'check-unfilled'} />
                            </Box>
                            <Typography
                              variant="body1"
                              component="span"
                              color="backgroundColors.brand"
                              sx={{
                                ml: 3,
                                textTransform: 'capitalize',
                                fontStyle: 'normal',
                                fontWeight: isSelected ? 'bold' : 'normal',
                              }}
                            >
                              {/* Remove any text between parentheses, e.g. (Customized) */}
                              {section.name?.replace(/\s*\([^)]*\)/g, '')}
                            </Typography>
                          </Box>
                        </ListItem>
                      );
                    })}
                </ul>
              </TutorialStep>
            </Box>
          </LayoutWrapper.LeftSidebar>
          <LayoutWrapper.MainContent>
            <Box sx={{ flex: 1, overflowY: 'scroll', pb: 2 }}>
              {noteData.content && (
                <Box sx={{ my: 2, mx: { xs: 1, sm: 2 } }}>
                  <TutorialStep
                    step={0}
                    description={`Welcome to a JotPsych encounter note! This top area of the interface shows you information about the encounter for which this note was created.`}
                    placement="bottom"
                  >
                    <Box className="mb-4 flex flex-col md:flex-row">
                      <Box className="flex-auto">
                        <Box sx={{ mb: 0.5, ml: 2 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: 0.5,
                            }}
                            onDoubleClick={() => !isEditingTitle && setIsEditingTitle(true)}
                          >
                            {isEditingTitle ? (
                              <>
                                <TextField
                                  value={editedTitle}
                                  onChange={e => setEditedTitle(e.target.value)}
                                  variant="standard"
                                  fullWidth
                                  onKeyPress={e => {
                                    if (e.key === 'Enter') {
                                      handleUpdateTitle();
                                    }
                                  }}
                                 slotProps={{ input: {
                                    style: {
                                      fontSize: '3rem',
                                      fontWeight: 600,
                                      color: theme.palette.text.primary,
                                    },
                                  } }}/>
                                <IconButton onClick={handleUpdateTitle} size="small" sx={{ ml: 1 }}>
                                  <CheckIcon />
                                </IconButton>
                              </>
                            ) : (
                              <Tooltip title="Double click to edit title" arrow placement="right">
                                <Typography
                                  variant="h3"
                                  sx={{
                                    color: 'textColors.darkHeader',
                                    fontWeight: '600',
                                    fontSize: {
                                      xs: '1.5rem',
                                      sm: '2rem',
                                      md: '2.5rem',
                                      lg: '3rem',
                                    },
                                  }}
                                >
                                  {noteData.note_title}
                                </Typography>
                              </Tooltip>
                            )}
                          </Box>
                          {!demoMode && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {patientData === undefined ? (
                                <Skeleton variant="text"  sx={{ width: 200, height: 30 }}/>
                              ) : patientData ? (
                                <PatientUnassignment
                                  patientData={{
                                    patient_id: patientData.patient_id ?? '',
                                    first_name: patientData.first_name ?? '',
                                    last_name: patientData.last_name ?? '',
                                  }}
                                  noteId={noteId || ''}
                                  onUnassign={handleUnassign}
                                  onUnassignError={handleUnassignError}
                                />
                              ) : (
                                <Box sx={{ mt: 1, mb: 2 }}>
                                  <AssignNoteToPatient
                                    noteId={noteId || ''}
                                    onAssign={handleAssign}
                                    onAssignError={handleAssignError}
                                  />
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box sx={{ mb: 2, ml: 2 }}>
                            <NoteMetadata
                              noteId={noteId || ''}
                              noteData={noteData}
                              encounterData={encounterData}
                              editable={noteData?.completion_status !== 'completed'}
                            />
                          </Box>
                        </Box>
                        <Divider />
                        {/* {actionsCount > 0 &&
                          userState?.featureFlags?.actions && (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "center",
                              }}
                            >
                              <Box
                                height="350px"
                                sx={{ mt: 2, maxWidth: "600px", width: "100%" }}
                              >
                                <ActionsNoteCard noteId={noteId || ""} />
                              </Box>
                            </Box>
                          )} */}
                        {matchedWords.length > 0 && (
                          <Box
                            sx={{
                              bgcolor: '#FFDBDA',
                              border: 1,
                              borderColor: '#E66260',
                              borderRadius: '0.75rem',
                              padding: '1rem 1.5rem',
                              width: '100%',
                              marginTop: '1.5rem',
                              position: 'relative',
                            }}
                          >
                            {/* Bell emoji with absolute positioning */}
                            <span
                              style={{
                                position: 'absolute',
                                top: '-10px',
                                left: '-10px',
                                fontSize: '24px',
                                // backgroundColor: '#EC8583',
                                backgroundColor: 'white',
                                borderRadius: '50%',
                                padding: '5px',
                                paddingRight: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '30px',
                                height: '30px',
                                boxShadow: '0px 0px 5px rgba(0,0,0,0.2)',
                              }}
                            >
                              👋
                            </span>
                            <Box>
                              {' '}
                              We've noticed potential spelling mistakes for{' '}
                              {formatMatchedWords([...matchedWords])} in your note. Our software
                              might confuse {matchedWords.length === 1 ? 'this' : 'these'} with
                              similar medication names, so please take a moment to double-check{' '}
                              {matchedWords.length === 1 ? 'it' : 'them'} for accuracy.
                            </Box>
                          </Box>
                        )}
                        {/* If summary exists, to be phased out in future versions of app */}
                        {noteData.content.summary && (
                          <Typography variant="body1" sx={{ color: 'textColors.primaryText' }}>
                            {textToParagraph({
                              text: noteData.content.summary,
                            })}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TutorialStep>
                  {viewMode.mode === 'note' && (
                    <TutorialStep
                      step={1}
                      description={`JotPsych encounter notes are broken down into sections. These sections and their content can be customized to your liking. Check out the 'Templates' tab after this tutorial.`}
                      placement="bottom"
                    >
                      <Box>
                        {noteData.content &&
                          noteData.content.sections.map((section, sectionIndex) => {
                            return (
                              <Box
                                key={sectionIndex}
                                id={`section-${sectionIndex}`}
                                style={{ scrollMarginTop: '4em' }}
                              >
                                {sectionIndex === 0 ? (
                                  <TutorialStep
                                    step={3}
                                    description="An individual section of the note can be copied, edited, and rated. Let's take a look at this first section."
                                  >
                                    {renderNoteSectionComponent({
                                      section,
                                      sectionIndex,
                                      startingTutorialStep: 4,
                                    })}
                                  </TutorialStep>
                                ) : (
                                  <>
                                    {renderNoteSectionComponent({
                                      section,
                                      sectionIndex,
                                    })}
                                  </>
                                )}
                              </Box>
                            );
                          })}

                        {/* Add/Remove Sections button - requires feature flag, permission, and note content */}
                        {showEditTemplate &&
                          noteData?.can_edit_template === true &&
                          noteData.template &&
                          noteData.content && (
                            <RoundedButton
                              fullWidth
                              variant="outlined"
                              sx={{
                                fontSize: '0.85rem',
                                fontWeight: '700',
                                whiteSpace: 'nowrap',
                                px: 2,
                                bgcolor: 'backgroundColors.secondary',
                                borderColor: 'borderColors.lightprimary',
                                border: '0.1px solid',
                              }}
                              onClick={() => {
                                // Track intent to edit template from sections footer
                                const templateInfo = extractTemplateInfo(noteData);
                                noteMetrics.templateEditClicked(noteId || '', {
                                  template_id: templateInfo?.id,
                                  template_name: templateInfo?.name,
                                  source: 'note_sections_footer',
                                  section_count: noteData?.content?.sections?.length || 0,
                                });

                                // Navigate to templates page with template ID to open editor
                                if (templateInfo?.id) {
                                  navigate(`/templates?id=${templateInfo.id}`);
                                } else {
                                  navigate('/templates');
                                }
                              }}
                            >
                              Add/Remove Sections
                            </RoundedButton>
                          )}

                        {!noteData?.is_rated && (
                          <NoteFeedbackBox
                            handleNoteCommentFeedback={(comment: string) => {
                              handleSubmitNoteCommentFeedback(comment);
                            }}
                            handleNoteRatingFeedback={(rating: number) => {
                              handleSubmitNoteRatingFeedback(rating);
                            }}
                          />
                        )}
                      </Box>
                    </TutorialStep>
                  )}
                  {viewMode.mode === 'transcript' && (
                    <>
                      {/* Notes section for text_notes_string */}
                      {((v2Note as any)?.text_notes_string ||
                        (noteData as any)?.text_notes_string) && (
                        <NotepadBox
                          marginLinePosition="35px"
                          contentPaddingLeft={5.5}
                          sx={{ mb: 4 }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              mb: 2,
                              fontWeight: 600,
                              color: 'textColors.darkHeader',
                            }}
                          >
                            Notes
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: 'textColors.primaryText',
                              whiteSpace: 'pre-wrap',
                              lineHeight: 1.6,
                            }}
                          >
                            {(v2Note as any)?.text_notes_string ||
                              (noteData as any)?.text_notes_string}
                          </Typography>
                        </NotepadBox>
                      )}
                      {noteData.transcript && (
                        <Transcript
                          transcript={noteData.transcript}
                          onCopy={(copiedText, copyMethod) => {
                            if (noteId) {
                              // Calculate total transcript character count
                              const totalLength = noteData.transcript
                                .map(segment => segment.text)
                                .join('').length;

                              // Calculate percentage of transcript copied
                              const textPercent =
                                totalLength > 0 ? (copiedText.length / totalLength) * 100 : 0;

                              noteMetrics.transcriptTextCopied(noteId, {
                                text_length: copiedText.length,
                                text_percent: Math.round(textPercent * 100) / 100, // Round to 2 decimal places
                                copy_method: copyMethod,
                                encounter_id: encounterId,
                              });
                            }
                          }}
                        />
                      )}
                    </>
                  )}
                  {debug && noteData && (
                    <pre className="text-black/40">{JSON.stringify(noteData.config, null, 4)}</pre>
                  )}
                </Box>
              )}
            </Box>
          </LayoutWrapper.MainContent>
          {/* <LayoutWrapper.RightSidebar>
            <ContentCard
              icon={<DrawOutlinedIcon fontSize="medium" sx={{ mr: 2 }} />}
              title="Current template"
              sx={
                isSwitchTemplateCard
                  ? {
                      minHeight: "420px",
                    }
                  : {
                      minHeight: "140px",
                    }
              }
              content={
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "bold",
                        color: "textColors.brand",
                        textDecorationColor: "inherit",
                      }}
                    >
                      {noteData.template && noteData.template.template_name}
                    </Typography>
                    <Button
                      sx={{
                        minWidth: "auto",
                        textTransform: "none",
                        textDecoration: "underline",
                        color: "textColors.darkText",
                        "&:hover": {
                          backgroundColor: "inherit",
                          textDecoration: "underline",
                        },
                      }}
                      onClick={() =>
                        setIsSwitchTemplateCard(!isSwitchTemplateCard)
                      }
                    >
                      Switch Template
                    </Button>
                  </Box>
                  <Collapse in={isSwitchTemplateCard}>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: "textColors.primary", mb: 2 }}
                    >
                      Would you like to regenerate this note with a different
                      template?
                    </Typography>
                    <TemplatePicker
                      onTemplateSelect={(template) => {
                        setTemplateIdToRegenerate(template?.template_id);
                      }}
                    />
                    <Box display="flex" justifyContent="center" mt={2} mb={2}>
                      <RoundedButton
                        variant="outlined"
                        sx={{ color: "textColors.brand" }}
                        onClick={() => {
                          setIsSwitchTemplateCard(false);
                        }}
                      >
                        Cancel
                      </RoundedButton>
                      <RoundedButton
                        variant="outlined"
                        sx={{ color: "textColors.brand" }}
                        onClick={() => {
                          handleRegenerateNoteWithNewTemplate();
                        }}
                        disabled={!templateIdToRegenerate || isRegeneratingNote}
                      >
                        {isRegeneratingNote ? (
                          <>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Regenerating...
                          </>
                        ) : (
                          "Regenerate"
                        )}
                      </RoundedButton>
                    </Box>
                    <Typography
                      variant="caption"
                      color={templateIdToRegenerate ? "red" : "textSecondary"}
                      sx={{ mt: 4, alignSelf: "flex-end" }}
                    >
                      The new note will be generated with the selected template.
                      This note will remain in your account unless you delete
                      it.
                    </Typography>
                  </Collapse>
                </>
              }
            />
            {userState?.featureFlags?.billing_data_collection &&
              !userState?.featureFlags?.billing_data_prediction && (
                <BillingSidebarDataCollection
                  noteId={noteId || ""}
                  billingSubmitted={
                    (note as unknown as NoteDataType)?.billing_data_collected
                  }
                />
              )}
            {userState?.featureFlags?.billing_data_prediction &&
              predictedCodesExtra && (
                <BillingSidebarDataPrediction
                  noteId={noteId || ""}
                  billingSubmitted={
                    (note as unknown as NoteDataType)?.billing_data_collected
                  }
                  status={predictedCodesExtra.status}
                  initialPredictedCodes={predictedCodesExtra.data}
                />
              )}
            <DismissableCard
              headerTitle={tutorialHeaderText}
              bodyText={[tutorialDescription]}
            /> */}
          {/* {billingCodeExtra && userState?.featureFlags?.billing_feature && (
            <BillingSidebar
              billingCodeExtra={billingCodeExtra}
              patientData={patientData}
              userState={userState}
              showTooltips={showTooltips}
              billingSubmitted={billingSubmitted}
              handleProviderInfoClick={handleProviderInfoClick}
              handlePatientInfoClick={handlePatientInfoClick}
              handlePatientEligibilityClick={handlePatientEligibilityClick}
              handleOpenBillingSidePane={handleOpenBillingSidePane}
              handleAssignPatientClick={handleAssignPatientClick}
              handleResubmitClaim={handleResubmitClaim}
            />
          )}
          {billingCodeExtra && userState?.featureFlags?.billing_test_gather && (
            <TestGatherBillingSidebarWithExtra
              billingCodeExtra={billingCodeExtra}
              patientData={patientData}
              showTooltips={showTooltips}
              billingSubmitted={billingSubmitted}
              handleProviderInfoClick={handleProviderInfoClick}
              handlePatientInfoClick={handlePatientInfoClick}
              handlePatientEligibilityClick={handlePatientEligibilityClick}
              handleOpenBillingSidePane={handleOpenBillingSidePane}
              handleAssignPatientClick={handleAssignPatientClick}
              handleResubmitClaim={handleResubmitClaim}
              noteId={noteId || ""}
            />
          )}
          {!billingCodeExtra && userState && (
            <>
              <TestGatherBillingSidebar
                userState={userState}
                noteId={noteId || ""}
              />
            </>
          )} */}
          {/* </LayoutWrapper.RightSidebar> */}
          <LayoutWrapper.BottomBar>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                overflowX: 'auto',
                alignItems: 'center',
              }}
            >
              <TutorialStep
                step={7}
                description="When you're done reviewing and editing a note, you can mark it as complete or delete it. When you delete a note, JotPsych permanently deletes it forever. So be careful!"
                placement="top"
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    minWidth: 'max-content', // Ensure content doesn't wrap
                    mr: 4,
                    mb: 1,
                  }}
                >
                  <Tooltip
                    title={
                      noteData.is_signed ? "Signed notes can't be deleted" : ''
                    }
                  >
                    <span>
                      <RoundedButton
                        variant="contained"
                        disabled={noteData.is_signed}
                        sx={{
                          fontSize: '0.85rem',
                          fontWeight: '700',
                          px: 2,
                          color: 'textColors.brandContrast',
                          borderColor: 'borderColors.warning',
                          bgcolor: 'backgroundColors.warningDark',
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            bgcolor: 'backgroundColors.warningDark',
                            borderColor: 'borderColors.warning',
                          },
                        }}
                        onClick={() => setShowDeleteModal(true)}
                      >
                        <DeleteIcon
                          sx={{
                            color: 'textColors.brandContrast',
                            fontSize: '1rem',
                            mr: 1,
                          }}
                        />
                        Delete Note
                      </RoundedButton>
                    </span>
                  </Tooltip>
                  <RoundedButton
                    variant="contained"
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: '700',
                      whiteSpace: 'nowrap',
                      px: 2,
                    }}
                    color="primary"
                    onClick={
                      noteData.completion_status === 'completed'
                        ? handleReviewNote
                        : handleCompleteNote
                    }
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {noteData.completion_status === 'completed' ? (
                      <>
                        <Edit
                          sx={{
                            fontSize: '1rem',
                            mr: 1,
                          }}
                        />
                        {noteSigningEnabled &&
                        noteData.is_signed &&
                        patientData?.patient_id
                          ? 'Amend'
                          : 'Review and Edit'}
                      </>
                    ) : (
                      <>
                        {!isSubmitting && (
                          <Check
                            sx={{
                              fontSize: '1rem',
                              mr: 1,
                            }}
                          />
                        )}
                        {`${
                          noteSigningEnabled && patientData?.patient_id
                            ? 'Sign'
                            : 'Complete'
                        } Note`}
                      </>
                    )}
                  </RoundedButton>
                </Box>
              </TutorialStep>
            </Box>
          </LayoutWrapper.BottomBar>
        </LayoutWrapper>
        <SigningNoteOnboarding open={isOnboardingOpen} onClose={() => setIsOnboardingOpen(false)} />
      </>
    </TutorialProvider>
  );
}

export default NoteViewRevamp;
